<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import { ATTACHMENT_TYPES, MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';
import { useMessageTranslation } from 'dashboard/composables/useMessageTranslation';
import { useTranslationExpansion } from 'dashboard/composables/useTranslationExpansion';
import {
  useChineseDisplay,
  queueMissingTranslation,
} from 'dashboard/composables/useChineseDisplay';

const context = useMessageContext();
const { content, attachments, contentAttributes, messageType } = context;

const { translationContent: preferredTranslation } =
  useTranslations(contentAttributes);
const cachedTranslation = computed(() => {
  const attributes = contentAttributes.value || {};
  if (attributes.borderchatTranslation || attributes.borderchat_translation) {
    return attributes.translations?.zh_CN || null;
  }
  return preferredTranslation.value;
});
const {
  eligible: canTranslate,
  state: translationState,
  translation: translationContent,
  pending: translationPending,
  showRequest: showTranslationRequest,
  canRequest: canRequestTranslation,
  notice: translationNotice,
  requestLabel: translationRequestLabel,
  requestTranslation,
} = useMessageTranslation(context, cachedTranslation);

const { translationExpanded, expandTranslation, toggleTranslation } =
  useTranslationExpansion(context, translationContent);
const hasVisibleTranslation = computed(
  () =>
    canTranslate.value && Boolean(translationContent.value?.trim()) &&
    translationContent.value?.trim() !== content.value?.trim()
);
// With Chinese display on, a translated message shows only its translation.
const { showChinese } = useChineseDisplay();
const showsChinese = computed(
  () => showChinese.value && hasVisibleTranslation.value
);
// It also translates what the agent is reading: a message never translated is
// queued once for the background translation, which arrives as an update.
const route = useRoute();
const autoQueued = ref(false);
const neverTranslated = computed(
  () => translationState?.value?.status === 'untranslated'
);
watch(
  () => showChinese.value && canTranslate.value && neverTranslated.value,
  wanted => {
    if (
      wanted &&
      queueMissingTranslation(
        route?.params?.accountId,
        context.conversationId?.value,
        context.id?.value
      )
    ) {
      autoQueued.value = true;
    }
  },
  { immediate: true }
);
const displayNotice = computed(
  () =>
    translationNotice.value ||
    (autoQueued.value && showChinese.value && neverTranslated.value
      ? '正在翻译…'
      : '')
);

const translateMessage = async () => {
  if (!canRequestTranslation.value) return;
  expandTranslation();
  await requestTranslation();
};

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isImageOnlyMessage = computed(
  () => !content.value?.trim() && attachments.value?.length > 0 &&
    attachments.value.every(attachment => attachment.fileType === ATTACHMENT_TYPES.IMAGE)
);

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});
</script>

<template>
  <div class="flex w-fit min-w-0 max-w-full flex-col items-stretch gap-2">
    <!-- The AI control always sits beside the bubble's bottom edge; the bubble
      yields the width instead of wrapping the control onto its own line. -->
    <div
      class="flex min-w-0 max-w-full items-end gap-2"
      :class="{ 'flex-row-reverse': messageType === MESSAGE_TYPES.OUTGOING }"
    >
      <BaseBubble
        :frameless="isImageOnlyMessage"
        :class="isImageOnlyMessage ? 'p-0' : 'px-4 py-3'"
        data-bubble-name="text"
      >
        <div class="gap-3 flex flex-col">
          <span v-if="isEmpty" class="text-n-slate-11">
            {{ $t('CONVERSATION.NO_CONTENT') }}
          </span>
          <FormattedContent
            v-if="content"
            :content="showsChinese ? translationContent : content"
            :data-chinese-display="showsChinese || undefined"
          />
          <div
            v-if="hasVisibleTranslation && translationExpanded && !showsChinese"
            class="pt-3 border-t-2 border-n-slate-8"
          >
            <FormattedContent :content="translationContent" />
          </div>
          <div
            v-if="canTranslate && (displayNotice || showTranslationRequest)"
            class="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
            data-translation-controls
            :aria-busy="translationPending"
          >
            <span
              v-if="displayNotice"
              role="status"
              class="text-n-slate-11"
            >
              {{ displayNotice }}
            </span>
          </div>
          <AttachmentChips :attachments="attachments" class="gap-2" />
          <template v-if="isTemplate">
            <div
              v-if="contentAttributes.submittedEmail"
              class="px-2 py-1 rounded-lg bg-n-alpha-3"
            >
              {{ contentAttributes.submittedEmail }}
            </div>
          </template>
        </div>
      </BaseBubble>
      <button
        v-if="
          canTranslate &&
          !showsChinese &&
          (hasVisibleTranslation ||
            translationPending ||
            showTranslationRequest)
        "
        type="button"
        class="borderchat-translation-toggle inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap !rounded-full !border !border-solid !border-n-blue-6 !bg-n-solid-1 px-3 py-1.5 text-sm leading-5 !text-n-blue-11 hover:!bg-n-blue-2 disabled:cursor-wait disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
        :disabled="!hasVisibleTranslation && !canRequestTranslation"
        :aria-expanded="hasVisibleTranslation ? translationExpanded : undefined"
        :aria-busy="translationPending"
        :aria-label="translationExpanded ? '收起翻译' : 'AI翻译为中文'"
        :title="
          hasVisibleTranslation
            ? translationExpanded
              ? '收起翻译'
              : '展开中文翻译'
            : translationRequestLabel
        "
        data-translation-request
        @click.stop="
          hasVisibleTranslation
            ? toggleTranslation()
            : translateMessage()
        "
      >
        <span
          :class="
            translationPending
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-languages'
          "
          class="size-4"
          aria-hidden="true"
        />
        <span>AI</span>
      </button>
    </div>
    <div
      v-if="$slots.footer"
      class="flex w-full items-center justify-end gap-2"
      data-message-actions
    >
      <slot name="footer" />
    </div>
  </div>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
